import React, { useState, useEffect, useContext } from 'react';
import { Users, LogOut, Search, ChevronRight, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { getDisplayName } from '../../utils/displayName';
import { supervisorsAPI } from '../../services/api';
import { toast } from 'react-toastify';

const SupervisorDashboard = () => {
	const navigate = useNavigate();
	const { logout } = useContext(AppContext);
	const { user } = useAuth();
	const [agents, setAgents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await supervisorsAPI.getMyAgents();
				setAgents(Array.isArray(data) ? data : []);
			} catch (err) {
				toast.error('Failed to load agents');
				setAgents([]);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	const displayName = (a) => [a.firstName, a.lastName].filter(Boolean).join(' ').trim() || a.email || '—';

	const filteredAgents = agents.filter(
		(a) =>
			displayName(a).toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
			(a.email && a.email.toLowerCase().includes(searchQuery.toLowerCase().trim())) ||
			(a.agentId && a.agentId.toLowerCase().includes(searchQuery.toLowerCase().trim())),
	);

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10'>
				<div className='flex justify-between items-center mb-4'>
					<div>
						<h2 className='text-xl font-black uppercase text-white'>Supervisor</h2>
						<p className='text-xs text-white/60'>{user ? getDisplayName(user) : '—'}</p>
					</div>
					<button type='button' onClick={() => logout && logout()} className='glass-card p-3 rounded-xl'>
						<LogOut className='w-5 h-5 text-white' />
					</button>
				</div>
				{agents.length > 0 && (
					<div className='relative'>
						<Search className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50' />
						<input
							type='text'
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							placeholder='Search by name, email or agent ID...'
							className='w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 outline-none focus:border-[#00F5FF]/50'
						/>
					</div>
				)}
			</header>
			<main className='flex-1 overflow-y-auto p-6 no-scrollbar space-y-4'>
				<button
					type='button'
					onClick={() => navigate('/supervisor/reports')}
					className='w-full glass-card p-4 flex items-center gap-4 border-l-4 border-[#9C27B0] active:scale-[0.99] transition-transform text-left'
				>
					<div className='w-12 h-12 bg-[#9C27B0]/10 rounded-2xl flex items-center justify-center shrink-0'>
						<BarChart3 className='w-6 h-6 text-[#9C27B0]' />
					</div>
					<div className='flex-1 min-w-0'>
						<p className='font-black text-white'>Reports</p>
						<p className='text-xs text-white/60'>Payments, deliveries, and summaries for your agents</p>
					</div>
					<ChevronRight className='w-5 h-5 text-white/30 shrink-0' />
				</button>
				{loading ? (
					<p className='text-white/60'>Loading...</p>
				) : agents.length === 0 ? (
					<p className='text-white/60'>No agents assigned to you yet.</p>
				) : filteredAgents.length === 0 ? (
					<p className='text-white/60'>No agents match your search.</p>
				) : (
					<div className='space-y-4'>
						{filteredAgents.map((a) => (
							<div key={a.id} onClick={() => navigate('/supervisor/agent/' + a.id)} className='glass-card p-4 flex items-center gap-4 active:scale-[0.99] cursor-pointer'>
								<div className='w-12 h-12 bg-[#00F5FF]/10 rounded-2xl flex items-center justify-center'>
									<Users className='w-6 h-6 text-[#00F5FF]' />
								</div>
								<div className='flex-1'>
									<p className='font-bold text-white'>{displayName(a)}</p>
									<p className='text-sm text-white/60'>{a.email}</p>
									<p className='text-xs text-white/50'>Assigned: {a.assignedBills ?? 0} | Delivered: {a.deliveredBills ?? a.totalDelivered ?? 0}</p>
								</div>
								<ChevronRight className='w-5 h-5 text-white/30' />
							</div>
						))}
					</div>
				)}
			</main>
		</div>
	);
};

export default SupervisorDashboard;
